import { Check, Pencil, AlertTriangle } from 'lucide-react';
import OrderDetailSheet from '../../components/OrderDetailSheet';
import OrderFacts, { productLine } from '../../components/OrderFacts';
import OrderContactButtons from '../../components/OrderContactButtons';
import { resolveDeliveryFee } from '../../services/deliveryFees';
import { pickOrderStatus } from '../../constants/deliveryStatus';

const CONF_STATUS = {
  new:         { label: 'En attente',  color: '#c2410c', bg: '#fff7ed', border: '#fed7aa' },
  recall:      { label: 'À rappeler',  color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  unreachable: { label: 'Injoignable', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  confirmed:   { label: 'Confirmé',    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  cancelled:   { label: 'Annulé',      color: '#be123c', bg: '#fff1f2', border: '#fecdd3' },
};

export default function MerchantOrderSheet({
  selected,
  products,
  editQtyPrice,
  savingOrderEdit,
  statusConfig,
  setEditQtyPrice,
  onClose,
  onSaveEdit,
  storeFees = null,
}) {
  const fee = resolveDeliveryFee(selected, storeFees);
  const detailAmount = selected.amount ?? (() => {
    const product = products.find((item) => item.name.toLowerCase() === (selected.product_name || '').toLowerCase().trim());
    return product ? product.price * (selected.quantity || 1) : null;
  })();

  // Une seule pastille : celle qui informe à ce stade de la commande.
  const primaryStatus = pickOrderStatus(selected, statusConfig, CONF_STATUS);

  return (
    <OrderDetailSheet
      order={selected}
      statusConfig={statusConfig}
      onClose={onClose}
      primaryStatus={primaryStatus}
    >
      {/* Faits de la commande — même bloc que les fiches admin et confirmation */}
      <OrderFacts
        amount={detailAmount}
        lines={[
          { label: 'Adresse', value: selected.address || '—' },
          productLine(selected),
          { label: 'Livraison', value: `${fee.toLocaleString('fr-FR')} FCFA` },
          { label: 'Téléphone', value: selected.phone || '—' },
          {
            label: 'Ajoutée',
            value: selected.created_at
              ? new Date(selected.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : '—',
          },
        ]}
      />

      {/* Motif du retour */}
      {selected.status === 'failed' && selected.return_reason && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}>
          <AlertTriangle size={14} color="#c2410c" style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>Motif du retour</div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#7c2d12' }}>{selected.return_reason}</div>
          </div>
        </div>
      )}

      {/* Edit form or edit button */}
      {editQtyPrice ? (
        <div style={{ background: '#f0f4ff', borderRadius: '14px', padding: '16px', marginBottom: '14px', border: '1.5px solid #bfdbfe' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#173b78', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>
            Modifier la commande
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {[
              { key: 'client',       label: 'Client',   type: 'text', placeholder: 'Nom du client' },
              { key: 'phone',        label: 'Téléphone', type: 'tel',  placeholder: '77 000 00 00' },
              { key: 'address',      label: 'Adresse',  type: 'text', placeholder: 'Adresse de livraison' },
              { key: 'product_name', label: 'Produit',  type: 'text', placeholder: 'Nom du produit' },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>{label}</label>
                <input
                  type={type}
                  value={editQtyPrice[key]}
                  placeholder={placeholder}
                  onChange={(e) => setEditQtyPrice((f) => ({ ...f, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #bfdbfe', borderRadius: '9px', fontSize: '13px', fontWeight: 500, outline: 'none', boxSizing: 'border-box', background: 'white' }}
                  onFocus={(e) => { e.target.style.borderColor = '#173b78'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#bfdbfe'; }}
                />
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { key: 'quantity', label: 'Quantité',       type: 'number', min: '1' },
                { key: 'amount',   label: 'Montant (FCFA)', type: 'number', min: '0' },
              ].map(({ key, label, type, min }) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '5px' }}>{label}</label>
                  <input
                    type={type} min={min}
                    value={editQtyPrice[key]}
                    onChange={(e) => setEditQtyPrice((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #bfdbfe', borderRadius: '9px', fontSize: '14px', fontWeight: 600, outline: 'none', boxSizing: 'border-box', background: 'white' }}
                    onFocus={(e) => { e.target.style.borderColor = '#173b78'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#bfdbfe'; }}
                  />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setEditQtyPrice(null)} style={{ flex: 1, padding: '10px', borderRadius: '9px', border: '1.5px solid #e2e8f0', background: 'white', fontSize: '13px', color: '#6b7280', cursor: 'pointer', fontWeight: 500 }}>
              Annuler
            </button>
            <button onClick={onSaveEdit} disabled={savingOrderEdit} style={{ flex: 2, padding: '10px', borderRadius: '9px', border: 'none', background: savingOrderEdit ? '#e2e8f0' : 'linear-gradient(135deg, #173b78, #2a66b8)', color: savingOrderEdit ? '#94a3b8' : 'white', fontSize: '13px', fontWeight: 600, cursor: savingOrderEdit ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              {savingOrderEdit ? 'Enregistrement...' : <><Check size={13} /> Enregistrer</>}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setEditQtyPrice({
            client: selected.client || '', phone: selected.phone || '',
            address: selected.address || '', product_name: selected.product_name || '',
            quantity: String(selected.quantity || 1), amount: String(selected.amount || ''),
          })}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '11px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: 'white', color: '#173b78', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginBottom: '14px', transition: 'all 0.15s' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#173b78'; e.currentTarget.style.background = '#f0f4ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
        >
          <Pencil size={13} /> Modifier la commande
        </button>
      )}

      <OrderContactButtons phone={selected.phone} />
    </OrderDetailSheet>
  );
}
